import { Button } from 'components/Button/Button'
import { useCallback, useMemo, useRef, useState, useEffect } from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { slsClient } from 'clients/slsClient'
import { useDispatch, useSelector } from 'react-redux'
import { getUserData } from 'redux/reducers/users'
import { MySpinner } from 'components/MySpinner/MySpinner'
import { Logger } from 'utils/logger'
import { useMountedState } from 'hooks/useMountedState'
import { createSchema, DeleteAccountFormData } from 'models/forms/DeleteAccountForm'
import { handleAccountLocked } from 'utils/handle-account-lockout'
import { useHistory } from 'react-router'
import { MyTextFieldPassword } from 'components/ReactHookForm/MyTextFieldPassword'
import { MyInternalLink, MyLink } from 'components/MyLink'
import { medicareApiClient } from 'clients/medicareApiClient'
import { Alert } from '@cmsgov/ds-medicare-gov'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { Activity, Component, SubmissionType } from '@cms-max/medicare-api-sdk'
import { GenericError } from 'components/GenericError/GenericError'
import { MyDropdown } from 'components/ReactHookForm/MyDropdown'
import { MyChoiceList } from 'components/ReactHookForm/MyChoiceList'
import { MyTextField } from 'components/ReactHookForm/MyTextField'

export type DeleteAccountFormProps = {
  onSubmit: (password: string) => void,
  href?: string,
  onClose?: () => void
}

export enum ReasonValues {
  NO_VALUE = '',
  ACCIDENT = 'I didn\'t mean to create an account',
  TOO_MANY_EMAILS = 'I get too many emails',
  NOT_USEFUL = 'I couldn\'t find what I needed',
  ACCOUNT_ISSUE = 'I have a problem with my account',
  OTHER = 'Other'
}

type VerifyPasswordResponseState = 'initial' | 'loading' | 'locked' | 'success' | 'validation-error' | 'default-error'

const trackPasswordVerificationResponse = (respStatus: 'Failure' | 'Success') => {
  window.globalHeaderEvents?.handleEvent({
    core: {
      activity: Activity.DownstreamServiceResponse,
      property: 'MAX',
      component: Component.AccountSettings,
      elementId: 'accountsettings_verifypassword_response',
      additionalData: {
        result: respStatus
      },
      submissionType: SubmissionType.Verify
    }
  })
}

/**
 * @param props
 * @returns
 */
export const DeleteAccountForm = ({ onSubmit, onClose, href }: DeleteAccountFormProps) => {
  const flags = useFlags()
  const { t } = useTranslation('deleteAccount')
  const { user } = useSelector(getUserData)
  const [displayTellUsMore, setDisplayTellUsMore] = useState(false)
  const [displayEmailAlert, setDisplayEmailAlert] = useState(false)
  const [responseState, setResponseState] = useMountedState<VerifyPasswordResponseState>('initial')
  const [attemptsRemaining, setAttemptsRemaining] = useMountedState<string | undefined>(undefined)
  const alertContainerRef = useRef<HTMLDivElement>(null)
  const dispatch = useDispatch()
  const history = useHistory()
  const schema = useMemo(() => createSchema(t), [t])
  const defaultValues: Partial<DeleteAccountFormData> = {
    deleteConfirmation: String('no')
  }
  const methods = useForm<DeleteAccountFormData>({
    mode: 'onTouched',
    reValidateMode: 'onChange',
    defaultValues,
    resolver: zodResolver(schema)
  })
  const watchReasonDropdown = methods.watch('reason')
  const watchConfirmationRadio = methods.watch('deleteConfirmation')

  useEffect(() => {
    const displayTellUsMoreField = [
      ReasonValues.ACCIDENT.toString(),
      ReasonValues.ACCOUNT_ISSUE.toString(),
      ReasonValues.NOT_USEFUL.toString(),
      ReasonValues.OTHER.toString()
    ].includes(watchReasonDropdown)
    setDisplayTellUsMore(displayTellUsMoreField)

    const displayEmailAlertField = watchReasonDropdown === ReasonValues.TOO_MANY_EMAILS
    setDisplayEmailAlert(displayEmailAlertField)
  }, [watchReasonDropdown, watchConfirmationRadio, dispatch])

  const handleOnSubmit = useCallback(async (data:any) => {
    const handleError = () => {
      alertContainerRef.current?.focus()
    }

    if (user?.username) {
      setResponseState('loading')
      // Default to 'default-error' to reduce if/else logic in the try/catch below
      // If verify password returns false, or we get an unexpect error thrown, we use default-error
      let responseState: VerifyPasswordResponseState = 'default-error'
      try {
        if (await slsClient.updateAccountCookie(data.password)) {
          responseState = 'success'
        }
      } catch (error: unknown) {
        Logger.error(error)
        // Handle attempts remaining
        if (error instanceof Error) {
          if (error.message === 'user is locked') {
            responseState = 'locked'
          } else if (error.message === 'incorrect password' || error.message === 'missing password') {
            responseState = 'validation-error'
            const attemptsRemaining = await medicareApiClient.asyncGetPasswordAttemptsRemaining()
            setAttemptsRemaining(attemptsRemaining)
          }
        }
      }

      // Update the actual component state
      setResponseState(responseState)

      // Trigger side effects - activity tracking, focusing page elements, going to the update modal, etc.
      if (responseState === 'locked') {
        trackPasswordVerificationResponse('Failure')
        await handleAccountLocked(history, dispatch)
      } else if (responseState === 'validation-error' || responseState === 'default-error') {
        trackPasswordVerificationResponse('Failure')
        handleError()
      } else {
        trackPasswordVerificationResponse('Success')
        onSubmit(data.password)
      }
    } else {
      // This shouldn't happen because to get to account settings they need to be authenticated
      // Handled with the generic error
      setResponseState('default-error')
      handleError()
    }
  }, [
    dispatch,
    history,
    onSubmit,
    setResponseState,
    setAttemptsRemaining,
    user?.username
  ])

  const handleOnSubmitNoConf: SubmitHandler<any> = async () => {
    history.push('/account-settings')
  }

  return (
    <>
      <FormProvider {...methods}>
        <div ref={alertContainerRef} tabIndex={-1}>
          {responseState === 'default-error' && (
            <GenericError />
          )}
          {responseState === 'validation-error' && (
            <Alert variation='error'>
              <p>{t('content.invalidPassword.doesNotMatchOurRecords')}</p>
              {flags.displaySlsAttemptsRemaining && attemptsRemaining && (
                <p>{t('content.invalidPassword.youHaveXMoreTries', { attemptsRemaining })}</p>
              )}
            </Alert>
          )}
        </div>
        <form onSubmit={ watchConfirmationRadio === 'yes' ? methods.handleSubmit(handleOnSubmit) : handleOnSubmitNoConf }>
          <MyChoiceList
            variableInputName="deleteConfirmation"
            label={t('content.confirmQuestion')}
            errorMessageString=""
            choices={[
              {
                label: t('content.yesText'),
                value: 'yes'
              },
              {
                label: t('content.noText'),
                value: 'no'
              }
            ]}
            headingLevel='2'
          />
          <p>{t('content.requiredText')}</p>
          <div className="my-u-my--8">
            <MyTextFieldPassword
              name="password"
              label={t('content.passwordText')}
              labelClassName="my-u-text-transform--default"
              defaultValue=""
            />
            <MyDropdown
                name="reason"
                label={t('content.reasonText')}
                className="my-c-claim-filter--field my-u-flex-1"
                fieldClassName="ds-u-margin--0"
                labelClassName="my-u-text-transform--default"
                id="delete_account_reason"
                options={[
                  { value: ReasonValues.NO_VALUE, label: '' },
                  { value: ReasonValues.ACCIDENT, label: t('content.reasons.accident') },
                  { value: ReasonValues.TOO_MANY_EMAILS, label: t('content.reasons.tooManyEmails') },
                  { value: ReasonValues.NOT_USEFUL, label: t('content.reasons.notUseful') },
                  { value: ReasonValues.ACCOUNT_ISSUE, label: t('content.reasons.accountIssue') },
                  { value: ReasonValues.OTHER, label: t('content.reasons.other') }
                ]}
              />
              {displayTellUsMore && (
                  <>
                    <MyTextField
                      name='additionalInfo'
                      rules={{
                        required: { value: true, message: t('incompleteInformation.repFullName') },
                        pattern: { value: /^[a-zA-Z0-9 .'-]+$/, message: t('invalidRegex.genericSpecialCharactersError') }
                      }}
                      label={t('content.tellUsMoreText')}
                      labelClassName="my-u-text-transform--default"
                      maxLength={100}
                    />
                  </>
              )}
              {displayEmailAlert && (
                <>
                  <Alert heading={t('content.infoAlert.heading')} className="ds-u-margin-top--4">
                    <p>{t('content.infoAlert.alertContent')}</p>
                    <p>
                      <MyLink href='https://public.govdelivery.com/accounts/USCMSMYMEDICARE/subscriber/new'>
                        {t('content.infoAlert.alertContentLink')}
                      </MyLink>
                    </p>
                  </Alert>
                </>
              )}
          </div>
          <div className="ds-c-dialog__actions">
            <div className="ds-l-form-row">
              <div className="ds-l-col--12 ds-l-sm-col--auto my-u-my--2">
                <Button
                  type="submit"
                  className="ds-c-button ds-c-button--solid my-u-py--6 my-u-width--full my-u-whitespace-nowrap"
                >
                  {responseState === 'loading' && (
                    <MySpinner size="small" />
                  )}{' '}
                <span className="notranslate">{t('content.submitText')}</span>
                </Button>
              </div>
              {(href || onClose) && (
                <div className="ds-l-col--12 ds-l-sm-col--auto my-u-my--2">
                  {href
                    ? (
                        <MyInternalLink
                          className="ds-c-button my-u-py--6 my-u-width--full"
                          to={href}
                          onClick={onClose}
                        >
                          {t('content.cancelText')}
                        </MyInternalLink>
                      )
                    : (
                        <Button
                        className="ds-c-button my-u-py--6 my-u-width--full"
                        onClick={onClose}
                      >
                        {t('content.cancelText')}
                      </Button>
                      )
                  }
              </div>
              )}
            </div>
          </div>
        </form>
      </FormProvider>
    </>
  )
}
